/**
 * Estimator Wizard Component
 *
 * Main container for the multi-step estimation wizard.
 * Supports three modes: Full Room, Ceiling Only, and Individual Walls.
 */

import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useEstimator } from '../../context/EstimatorContext';
import { ValidationError } from '../../context/EstimatorContext';
import { useToast } from '../../context/ToastContext';
import { ErrorBoundary } from './ErrorBoundary';
import { StepIndicator } from '../layout/StepIndicator';
import { ModeStep } from './steps/ModeStep';
import { WallsStep } from './steps/WallsStep';
import { CeilingStep } from './steps/CeilingStep';
import { DimensionsStep } from './steps/DimensionsStep';
import { ApplicationStep } from './steps/ApplicationStep';
import { LeadCaptureStep } from './steps/LeadCaptureStep';
import { EstimateResults } from '../results/EstimateResults';
import {
  ChevronLeft,
  ChevronRight,
  Calculator,
  Loader2,
  MessageCircle,
} from 'lucide-react';
import {
  trackStepNavigation,
  trackVirtualPageView,
  trackCalculateClicked,
  CalculateClickedData,
} from '../../utils/gtmTracking';
import { GARAGE_DOOR_PRESETS } from '../../data/presets';
import { setErrorReportingStep } from '../../utils/errorReporting';
import { useInlineGleapLauncher } from '../../utils/gleapInline';
import './EstimatorWizard.css';

/** Height of the always-visible footer handle (Footer.css); the bar sticks just above it. */
const FOOTER_HANDLE_PX = 18;

const EstimatorWizardInner: React.FC = () => {
  const { t } = useTranslation('wizard');
  const { state, goToStep, nextStep, prevStep, calculateEstimate, canProceed, getStepOrder, getValidationErrors } = useEstimator();

  // Validation assist
  const { showToast } = useToast();

  const triggerValidationAssist = useCallback((errors: ValidationError[]) => {
    if (errors.length === 0) return;
    const first = errors[0];
    showToast(first.message, first.fieldId);
  }, []);

  const { currentStep, completedSteps, estimateResult, isCalculating, calculationError, calculationMode } = state;

  // --- GTM: EVENT 3 — Virtual page view on every step load ---
  const prevStepRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentStep !== prevStepRef.current) {
      prevStepRef.current = currentStep;
      trackVirtualPageView(currentStep, calculationMode);
    }
  }, [currentStep, calculationMode]);

  // Error reports carry the step the visitor was on when things broke.
  useEffect(() => {
    setErrorReportingStep(estimateResult ? 'results' : currentStep);
  }, [currentStep, estimateResult]);

  // --- GTM: EVENT 4 — Track calculate clicked after results arrive ---
  const justCalculatedRef = useRef(false);
  useEffect(() => {
    if (justCalculatedRef.current && estimateResult) {
      justCalculatedRef.current = false;

      const { wallsConfig, ceilingConfig, applicationConfig } = state;
      const garageWidth = applicationConfig.garage.width;
      const garageHeight = applicationConfig.garage.height;
      const matchedPreset = GARAGE_DOOR_PRESETS.find(
        p => p.width === garageWidth && p.height === garageHeight,
      );
      const garageDoorSize = applicationConfig.garage.enabled
        ? matchedPreset
          ? matchedPreset.label
          : `${garageWidth}x${garageHeight}_custom`
        : 'none';

      const data: CalculateClickedData = {
        mode: calculationMode,
        wallColor: wallsConfig.color,
        wallOrientation: wallsConfig.orientation,
        slatwallEnabled: wallsConfig.slatWallNumPanels > 0,
        ceilingIncluded: ceilingConfig.include,
        ceilingColor: ceilingConfig.color,
        spaceType: applicationConfig.spaceType,
        garageDoorEnabled: applicationConfig.garage.enabled,
        garageDoorCount: applicationConfig.garage.numDoors,
        garageDoorSize,
        estimateResult,
      };
      trackCalculateClicked(data);
    }
  }, [estimateResult, state, calculationMode]);

  // Handle final step submission
  const handleCalculate = () => {
    if (!canProceed) {
      const errors = getValidationErrors();
      triggerValidationAssist(errors);
      return;
    }
    justCalculatedRef.current = true;
    calculateEstimate();
  };

  // --- GTM: EVENT 2 — Step navigation ---
  const handleNext = () => {
    if (!canProceed) {
      const errors = getValidationErrors();
      triggerValidationAssist(errors);
      return;
    }
    const stepOrder = getStepOrder();
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      trackStepNavigation('next', currentStep, stepOrder[currentIndex + 1], calculationMode);
    }
    nextStep();
  };

  const handlePrev = () => {
    const stepOrder = getStepOrder();
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      trackStepNavigation('previous', currentStep, stepOrder[currentIndex - 1], calculationMode);
    }
    prevStep();
  };

  // If we have results, show the results page
  if (estimateResult) {
    return <EstimateResults />;
  }

  // Render current step content
  const renderStep = () => {
    switch (currentStep) {
      case 'mode':
        return <ModeStep />;
      case 'walls':
        return <WallsStep />;
      case 'ceiling':
        return <CeilingStep />;
      case 'dimensions':
        return <DimensionsStep />;
      case 'application':
        return <ApplicationStep />;
      case 'leadCapture':
        return <LeadCaptureStep />;
      default:
        return null;
    }
  };

  const stepOrder = getStepOrder();
  const isFirstStep = currentStep === stepOrder[0];
  const isLastStep = currentStep === stepOrder[stepOrder.length - 1];

  return (
    <div className="container">

      <StepIndicator
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={goToStep}
        stepOrder={stepOrder}
      />

      <div className="wizard-content card shadow">
        {renderStep()}

      </div>

      <WizardNavBar
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        isCalculating={isCalculating}
        onPrev={handlePrev}
        onNext={handleNext}
        onCalculate={handleCalculate}
      />

    </div>
  );
};

interface WizardNavBarProps {
  isFirstStep: boolean;
  isLastStep: boolean;
  isCalculating: boolean;
  onPrev: () => void;
  onNext: () => void;
  onCalculate: () => void;
}

/**
 * Previous / Next|Calculate bar. Sticky: it rides the bottom of the screen
 * (above the footer handle) while the step is taller than the viewport, and
 * settles under the card at the end. Gets a shadow only while stuck.
 *
 * While mounted it takes over Gleap's launcher: the floating bubble is hidden
 * (it covered Next on phones) and chat opens from the button in the bar.
 */
const WizardNavBar: React.FC<WizardNavBarProps> = ({ isFirstStep, isLastStep, isCalculating, onPrev, onNext, onCalculate }) => {
  const { t } = useTranslation('wizard');
  const gleap = useInlineGleapLauncher();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [stuck, setStuck] = useState(false);

  // The sentinel sits right after the bar, at its natural resting place. When
  // that place is below the visible area (minus the footer handle) the bar is
  // pinned to the bottom, i.e. stuck.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      const bottom = entry.rootBounds?.bottom ?? window.innerHeight;
      setStuck(!entry.isIntersecting && entry.boundingClientRect.top >= bottom);
    }, { rootMargin: `0px 0px -${FOOTER_HANDLE_PX}px 0px` });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div className={`wizard-navigation${stuck ? ' wizard-navigation--stuck' : ''}`}>
        <button
          type="button"
          className="btn btn-secondary d-inline-flex align-items-center gap-1 wizard-nav-prev"
          onClick={onPrev}
          disabled={isFirstStep}
        >
          <ChevronLeft size={18} />
          {t('nav.previous')}
        </button>

        {gleap.available && (
          <button
            type="button"
            className="btn btn-outline-secondary rounded-circle d-inline-flex align-items-center justify-content-center wizard-nav-chat"
            onClick={gleap.open}
            aria-label={t('nav.chat')}
            title={t('nav.chat')}
          >
            <MessageCircle size={20} />
          </button>
        )}

        {isLastStep ? (
          <button
            type="button"
            className="btn btn-primary btn-lg d-inline-flex align-items-center gap-1 wizard-nav-calc"
            onClick={onCalculate}
            disabled={isCalculating}
          >
            {isCalculating ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                {t('nav.calculating')}
              </>
            ) : (
              <>
                <Calculator size={20} />
                {t('nav.calculate')}
              </>
            )}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary d-inline-flex align-items-center gap-1 wizard-nav-next"
            onClick={onNext}
          >
            {t('nav.next')}
            <ChevronRight size={18} />
          </button>
        )}
      </div>
      <div ref={sentinelRef} className="wizard-navigation-sentinel" aria-hidden="true" />
    </>
  );
};

export const EstimatorWizard: React.FC = () => {
  // Without onReset the boundary's button only re-rendered the same state, so a
  // deterministic crash came straight back. The context reset also clears the
  // sessionStorage snapshot, so a bad restored state can't recur either.
  const { reset } = useEstimator();
  return (
    <ErrorBoundary scope="wizard" onReset={reset}>
      <EstimatorWizardInner />
    </ErrorBoundary>
  );
};

export default EstimatorWizard;
